import tinycolor from 'tinycolor2'
import settings from '../../settings'
import { isJSON, parseJSONSafely } from '../../util/helpers'

const {
  wrapperElementDataAttribute,
  wrapperElementId,
  LOCALE_IN_USE,
  googleFontCDN,
  BUILD_DOMAIN,
  ACCEPTABLE_MAIN_DOMAINS,
} = settings

const systemFont =
  LOCALE_IN_USE === 'fa'
    ? 'dana'
    : ['-apple-system', 'BlinkMacSystemFont', 'ff-mark-pro', 'sans-serif'].join(
        ','
      )

export function slugExistsOnWrapper() {
  const wrapper = document.getElementById(wrapperElementId)
  if (!wrapper) return false

  const formSlug = wrapper.getAttribute(`data-${wrapperElementDataAttribute}`)
  return Boolean(formSlug)
}

export const formIsEmbeddedScript = (
  formAddress,
  subdomain,
  customDomain,
  ownerCustomDomain
) => {
  const windowOrigin = window.location.origin.replace(/^https?:\/\//, '')
  const windowHost = window.location.host
  const windowPathname = window.location.pathname
  const acceptableMainDomains = isJSON(ACCEPTABLE_MAIN_DOMAINS)
    ? [...JSON.parse(ACCEPTABLE_MAIN_DOMAINS), BUILD_DOMAIN]
    : [BUILD_DOMAIN]

  /* ----- */
  const isOnCustomDomain = [customDomain, ownerCustomDomain].some(
    (cname) => cname === windowOrigin
  )

  const isOnSubdomain = acceptableMainDomains.some(
    (domain) =>
      `${windowHost}${windowPathname}` ===
      `${subdomain}.${domain}/${formAddress}`
  )

  const isOnMainDomain = acceptableMainDomains.some(
    (domain) => `${windowHost}${windowPathname}` === `${domain}/${formAddress}`
  )

  /* ----- */
  if (!isOnMainDomain && !isOnSubdomain && !isOnCustomDomain) return true
  return false
}

export function isCrossOriginFrame() {
  try {
    return !window.top.location.hostname
  } catch (e) {
    return true
  }
}

const applyStylesToStyleSheet = (styleSheet, cssRules) => {
  for (let index = 0; index < cssRules.length; index++) {
    styleSheet.insertRule(cssRules[index], styleSheet.cssRules.length)
  }
}

export const addGeneralTheme = (
  styleSheet,
  rgbaColor = 'rgba(255,255,255,1)',
  themeBackgroundImage = false,
  isMultiStep = false,
  themeConfigs
) => {
  if (!styleSheet) return
  const isModernView = themeConfigs.modern_view

  const fullPageBackground = Boolean(
    themeConfigs.background_type === 'full_page' ||
      !themeConfigs.background_type ||
      (!isModernView && isMultiStep)
  )
  const hasShadowBackground = Boolean(
    themeConfigs.background_shadow && fullPageBackground
  )

  const fontName = themeConfigs.google_font
    ? themeConfigs.google_font.split('+').join(' ')
    : systemFont

  const rules = [
    `#main-form,
      #formz-wrapper .form-theme-background {
      transition: all 0.2s linear;
    }`,
    `#formz-wrapper,
     #formz-wrapper *
     {
      font-family: ${fontName};
    }`,
  ]

  if (themeBackgroundImage && fullPageBackground) {
    rules.push(`
      #formz-layout-wrapper {
        background: url(${themeBackgroundImage});
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
        background-attachment: fixed;
      }`)
  } else {
    rules.push(
      `#formz-layout-wrapper,
      #formz-wrapper .form-theme-background {
        background: ${rgbaColor};
      }`,
      `.formz-layout-wrapper {
         background-color: ${rgbaColor};
      }`
    )
  }
  if (themeBackgroundImage && hasShadowBackground) {
    rules.push(`
      #formz-layout-wrapper::after {
        content: '';
        display: block;
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        background-color: ${tinycolor(
          themeConfigs.background_shadow
        ).toHex8String()};
        opacity: 0.5;
      }
    `)
  }
  if (isMultiStep) {
    rules.push(`body {
      overscroll-behavior: none;
    }`)
  }

  // styleSheet.insertRule(rule, styleSheet.cssRules.length)
  applyStylesToStyleSheet(styleSheet, rules)
}

/* 
  reserved classnames for adding background and border color based on form's theme :
  1- form-theme-input
  2- form-theme-field-background
  3- form-theme-dark-field-background
  4- form-theme-field-border
*/
export const addContentTheme = (
  styleSheet,
  rgbaColor = 'rgba(0,0,0,0.05)',
  border
) => {
  if (!styleSheet) return

  const color = tinycolor(rgbaColor)

  const borderColor = tinycolor(border)
  const darkened = tinycolor(rgbaColor).darken(50).toRgbString()
  const lightDark = tinycolor(rgbaColor).darken(10).toRgbString()

  const rules = [
    `#formz-wrapper .form-theme-input  {
      background-color: ${rgbaColor};
      border: 1px solid ${borderColor.toHexString()};
    }`,
    `#formz-wrapper .form-theme-input input[type="radio"] + label svg {
      fill: ${color.lighten(30).toHexString()};
      transition: 0.4s;
      stroke: ${color.darken(30).toHexString()};
      stroke-width: 15px;
    }`,
    `#formz-wrapper .form-theme-input .form-theme-score > label:hover {
        background: ${darkened};
        color: ${color.toRgbString()};
      }`,
    `#formz-wrapper .form-theme-input .form-theme-score--selected > label {
      background: ${color.darken(50).toHexString()};
      color: ${color.lighten(50).toHexString()};
    }`,
    `#formz-wrapper .form-theme-input label:hover svg {
      fill: ${color.darken(30).toHexString()};
    }`,
    `#formz-wrapper .form-theme-input input:checked + label svg {
      fill: ${color.darken(30).toHexString()}
   }`,
    `#formz-wrapper .form-theme-field-background {
      background-color: ${rgbaColor};
    }`,
    `#formz-wrapper .form-theme-dark-field-background {
      background-color: ${lightDark};
    }`,
    `#formz-wrapper .form-theme-field-border {
      border-color: ${borderColor.toHexString()};
    }`,
    `#formz-wrapper .form-theme-scrollable::-webkit-scrollbar-thumb  {
      background: ${color.lighten(50).toHexString()};
    }`,
    `#formz-wrapper .formz-radio-svg-input-selected svg {
    fill: ${color.darken(30).toHexString()}
    }`,

    `#formz-wrapper .form-theme-otp-button  {
      color: ${rgbaColor}
    }`,
    `#formz-wrapper .styles_react-code-input__CRulA > input  {
      border-color: ${rgbaColor};
      color: ${rgbaColor};
    }`,
    `#formz-wrapper .verification-icon {
      fill: ${rgbaColor};
    }`,
    `#formz-wrapper .choice-other-option-input {
       border-bottom: 1px solid ${borderColor.toHexString()};
    }`,
    `#formz-wrapper .react-tel-input .flag-dropdown.open  {
      background: ${lightDark};
    }`,
  ]

  applyStylesToStyleSheet(styleSheet, rules)
}

/* 
  reserved classnames for adding text & background color to buttons based on form's theme :
  1- form-theme-button
  2- form-theme-button--as-text

  3- form-modern-input
  4- form-modern-primary
*/
export const addButtonTheme = (styleSheet, rgbaColor = '#858585', text) => {
  if (!styleSheet) return
  const color = tinycolor(rgbaColor)
  const textColor = tinycolor(text)
  const light = tinycolor(rgbaColor).lighten(10).toRgbString()
  const lighten = tinycolor(rgbaColor).lighten(30).toRgbString()
  const dark = tinycolor(rgbaColor).darken(10).toRgbString()
  const transparentColor = tinycolor(rgbaColor).setAlpha(0.2).toRgbString()

  const rules = [
    `#formz-wrapper .form-theme-button {
      color: ${textColor.toRgbString()};
      background-color: ${color.toHex8String()};
    }`,
    `#formz-wrapper .form-theme-button:hover {
      color: ${textColor.toRgbString()};
      background-color: ${dark};
    }`,
    `#formz-wrapper .form-theme-outlined-button {
      color: ${color.toHex8String()};
      transition: background-color 0.15s ease-out;
    }`,
    `#formz-wrapper .form-theme-outlined-button:hover {
      background-color: ${transparentColor};
    }`,
    `#formz-wrapper .form-theme-button--as-text {
      color: ${color.toHex8String()};
    }`,
    `#formz-wrapper .form-position-fixed.form-page-bottom-row {
      color: ${color.toHex8String()};
    }`,
    `#formz-wrapper .form-modern-input:focus {
      border-color: ${color.toHex8String()} !important;
    }`,
    // modern theme
    `#formz-wrapper .form-modern-button:disabled {
      background-color: ${light} !important;
    }`,
    `#formz-wrapper .form-modern-primary{
      color: ${color.toHex8String()};
    }`,
    `#formz-wrapper .form-modern-primary-bg{
      background-color: ${lighten};
    }`,
    `#formz-wrapper .form-modern-primary-textField-focus{
      border: 2px solid ${color.toHex8String()};
    }`,
    `#formz-wrapper .form-modern-score-option__selected {
      color: ${textColor.toRgbString()};
      background-color: ${color.toHex8String()};
    }`,
    `#formz-wrapper .form-modern-score-option:hover {
      background-color: ${light};
      color: ${textColor.toRgbString()};
    }`,
    `#formz-wrapper .form-modern-nps-icon:not(.form-modern-nps-icon--filled):hover {
      fill: ${light};
      stroke: ${light};
    }`,
    `#formz-wrapper .form-modern-nps-icon--filled:not(.form-modern-nps-likeDislike) {
      fill: ${color.toHex8String()};
      stroke: ${color.toHex8String()};
    }`,
    `#formz-wrapper .form-modern-nps-icon--filled.form-modern-nps-likeDislike svg>path {
      fill: ${color.toHex8String()};
      stroke: none;
      stroke-width: 1px;
    }`,
    `#formz-wrapper .form-modern-nps-likeDislike:hover svg>path {
      fill: ${color.toHex8String()};
      stroke: none;
      stroke-width: 1px;
    }`,
    `#formz-wrapper .formz-radio-label.formz-modern-radio-label::before {
      border-color: ${color.toHex8String()};
    }`,
    `#formz-wrapper .formz-radio input[type=radio]:checked + .formz-modern-radio-label::before {
      background-color: ${color.toHex8String()};
    }`,
  ]

  applyStylesToStyleSheet(styleSheet, rules)
}

/* 
  reserved classnames for adding text color based on form's theme :
  1- form-theme-text--main
  2- form-theme-text--lighten
  3- form-theme-text--main-as-background
  4- form-theme-checkbox
*/
export const addTextTheme = (styleSheet, rgbaColor = '#555555') => {
  if (!styleSheet) return

  const color = tinycolor(rgbaColor)
  const light = tinycolor(rgbaColor).lighten(30).toRgbString()
  const lighten = tinycolor(rgbaColor).lighten(20).toRgbString()
  const transparentColor = tinycolor(rgbaColor).setAlpha(0.5).toRgbString()

  const rules = [
    `#formz-wrapper .form-theme-input  {
      color: ${color.toRgbString()};
    }`,
    `#formz-wrapper .form-theme-input > input {
      color: ${color.toRgbString()};
    }`,
    `#formz-wrapper .form-theme-input::placeholder, #formz-wrapper .form-theme-input input::placeholder {
      color: ${transparentColor};
    }`,
    `#formz-wrapper .form-theme-text--main {
      color: ${color.toRgbString()};
    }`,
    `#formz-wrapper .form-theme-text--lighten {
      color: ${light};
    }`,
    `#formz-wrapper .form-theme-text--main-as-background {
      background-color: ${lighten};
    }`,
    `#formz-wrapper .form-theme-input input[type="radio"] + label svg {
      stroke: ${color.toHex8String()}
    }`,
    `#formz-wrapper .form-theme-input input:checked + label svg {
      fill: ${color.toHex8String()}
   }`,
    `#formz-wrapper .formz-radio-svg-input-selected svg {
      fill: ${color.toHex8String()}
    }`,
    `#formz-wrapper .form-theme-nps-icon:not(.form-modern-nps-icon--filled) {
      stroke: ${color.toHex8String()}
    }`,
    `#formz-wrapper .form-theme-nps-icon--filled {
    fill: ${color.toHex8String()}
    }`,
    `#formz-wrapper .formz-nps-group-container .nps-radio-name-wrapper .nps-radio-name {
    color: ${color.toHex8String()}
    }`,
    `#formz-wrapper .form-theme-input label:hover svg {
    fill: ${color.toHex8String()}
    }`,
    `#formz-wrapper .form-theme-input .form-theme-score > label:hover {
      background-color: ${color.toHex8String()};
    }`,
    `#formz-wrapper .form-theme-input .form-theme-score--selected > label {
    background: ${color.toHexString()};
  }`,
    `#formz-wrapper .form-warning-message ul li{
    color: ${color.toHexString()};
  }`,
    `#formz-wrapper .form-warning-message ul li a{
    color: ${color.toHexString()};
  }`,

    `#formz-wrapper .form-theme-input #selectArrow {
    fill: ${color.toHexString()};
  }`,
    `#formz-wrapper .form-theme-otp-button  {
    background-color: ${color.toHexString()}
  }`,
    `#formz-wrapper .form-theme-checkbox .formz-label__check{
       border: 1px solid ${transparentColor};
       background: transparent;
    }`,
    `#formz-wrapper .form-theme-checkbox .formz-label__check .formz-icon > svg path{
       fill: ${transparentColor} !important;
    }`,
    `#formz-wrapper .form-theme-checkbox .formz-label__checkbox:checked + .normal-label-values-container .formz-label__check_selected_customize .formz-icon > svg path{
       fill: ${color.toHexString()} !important;
    }`,

    `#formz-wrapper .react-tel-input .form-control  {
      color: ${color.toRgbString()};
    }`,
    `#formz-wrapper footer, #formz-wrapper footer *  {
      color: ${color.toRgbString()};
    }`,
  ]

  applyStylesToStyleSheet(styleSheet, rules)
}

export const addThemeCssTag = () => {
  const cssTagId = 'theme-css-tag'
  const cssTag = document.getElementById('theme-css-tag')

  if (!cssTag) {
    return addCssTag(cssTagId)
  } else {
    return cssTag.sheet
  }
}

export const convertRgbaToString = (
  parsedColor,
  initialColor = 'rgba(255,255,255,1)'
) => {
  if (parsedColor) {
    const { r, g, b, a = 1 } = parsedColor
    return `rgba(${r}, ${g}, ${b}, ${a})`
  }
  return initialColor
}

export const convertToHexColor = (themeColorJson) => {
  const rgbaColor = tinycolor(
    convertRgbaToString(parseJSONSafely(themeColorJson, null))
  )
  return rgbaColor.toHexString()
}

const addCssTag = () => {
  const styleEl = document.createElement('style')
  styleEl.setAttribute('id', 'theme-css-tag')
  styleEl.setAttribute('type', 'text/css')
  document.head.appendChild(styleEl)
  return styleEl.sheet
}

export const attachExternalCDNs = (themeConfigs) => {
  if (themeConfigs && themeConfigs.google_font) {
    const link1 = document.createElement('link')
    const link2 = document.createElement('link')
    link1.setAttribute('rel', 'preconnect')
    link1.setAttribute('href', googleFontCDN)
    link2.setAttribute(
      'href',
      `${googleFontCDN}/css2?family=${themeConfigs.google_font}`
    )
    link2.setAttribute('rel', 'stylesheet')
    document.head.appendChild(link1)
    document.head.appendChild(link2)
  }
}

export const setCustomCss = (styles) => {
  /* Create style element */
  var css = document.createElement('style')
  css.type = 'text/css'
  css.id = 'custom-css'
  if (css.styleSheet) css.styleSheet.cssText = styles
  else css.appendChild(document.createTextNode(styles))

  /* Append style to the head element */
  document.head.appendChild(css)
}

const cloneScript = (element) => {
  if (!element) return null

  const newTag = document.createElement(element.localName)

  // copy attributes
  const attrs = element.attributes
  for (let i = 0; i < attrs.length; i++) {
    newTag.setAttribute(attrs[i].name, attrs[i].value)
  }

  // copy inner text of script
  newTag.text = element.text

  return newTag
}

export const setCustomJs = (customJs) => {
  if (!customJs) return null
  try {
    const parser = new DOMParser()
    const doc = parser.parseFromString(customJs, 'text/html')
    const collection = doc.head.children
    Array.from(collection)
      .map((element) => cloneScript(element))
      .forEach((element) => document.head.appendChild(element))
  } catch (e) {
    console.error(e)
  }
}

export const removeCustomCss = () => {
  const head = document.getElementsByTagName('head')[0]
  const styleTag = document.getElementById('custom-css')
  if (styleTag && head) {
    head.removeChild(styleTag)
  }
}
